/* eslint no-use-before-define: 0 */
import pick from 'lodash/pick';
import { replace } from 'connected-react-router';
import axios from 'axios';

import {
  defaultState,
  RESET,
  FETCH_PRODUCTS_LOADING,
  FETCH_PRODUCTS_ERROR,
  FETCH_PRODUCTS_DATA,
  SET_PRODUCT_ID,
  SET_PRODUCT_TYPE,
  SET_POLLUTANT,
  SET_SECTOR,
  SET_CITY,
  SET_TIMESET,
  SET_YEAR,
  SET_MONTH,
  SET_LANGUAGE,
  SET_COMPARING_YEAR,
  SET_COMPARING_MONTH,
  SET_STATE,
  SET_BASEMAP,
  SET_LABELS,
  SET_VIEWPORT,
} from './reducer';

import {
  getAreas,
  getSelectedArea,
  getValidProducts,
  getSectors,
  getSelectedSector,
  getMonths,
  getSelectedMonth,
  getPollutants,
  getSelectedPollutant,
  getYears,
  getSelectedCity,
  getSelectedYear,
  getComparingMonths,
  getSelectedComparingMonth,
  getSelectedComparingYear,
  getComparingYears,
  getSelectedProductType,
} from "./selectors";

/**
 * Reset the store
 */
export const reset = () => ({ type: RESET });

/**
 * Fetch the list of products
 */
export const fetchProducts = () => (dispatch, getState) => {
  dispatch({ type: FETCH_PRODUCTS_LOADING, payload: true });
  dispatch({ type: FETCH_PRODUCTS_ERROR, payload: false });

  axios({
    url: `${process.env.REACT_APP_API_URL}/products?&isServerRenderable=true`,
    method: 'GET',
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json; charset=utf-8'
    },
  })
    .then((response) => {
      if (response.status >= 200) {
        dispatch({ type: FETCH_PRODUCTS_LOADING, payload: false });
        dispatch({ type: FETCH_PRODUCTS_DATA, payload: response.data });
      } else {
        console.error(response.statusText);
        dispatch({ type: FETCH_PRODUCTS_ERROR, payload: true });
      }
    })
    .catch((err) => {
      console.error(err);
      dispatch({ type: FETCH_PRODUCTS_ERROR, payload: true });
    })
    .then(() => {
      const areas = getAreas(getState());
      const selectedArea = getSelectedArea(getState());

      if (!selectedArea) {
        // If there's no selected area or the productId present in the URL
        // isn't present in the results of the API, we set one by default
        dispatch(setArea(areas[0]));
      } else {
        // If we have a selected area it's because we've previously
        // restored the state and the productId still exits
        // In that case we want to make sure the state is valid. An easy
        // way is to set the area to it's current value, triggering each
        // of the selectors to do the same. If one of them has a wrong
        // value, they will be reset to a default one.
        dispatch(setArea(selectedArea));
      }
    });
};


export const setProductType = (product, resetDefault = false) => (dispatch, getState) => {

  if (product) {
    dispatch({ type: SET_PRODUCT_ID, payload: product.id });
    dispatch({ type: SET_PRODUCT_TYPE, payload: product.id });

    const pollutants = getPollutants(getState());
    const selectedPollutant = getSelectedPollutant(getState());

    // We give:
    // - either a default value for the pollutant
    // - either we need to reset the pollutant because the option is not available anymore
    if (!selectedPollutant || resetDefault) {
      let pollutant = pollutants.find(pollutant => pollutant.value === 'PM25');
      let sector;

      dispatch(setPollutant(pollutant, sector, resetDefault));
    }

    const years = getYears(getState());
    const selectedYear = getSelectedYear(getState());


    // We give:
    // - either a default value for the year
    // - either we need to reset the year because the option is not available anymore
    if (!selectedYear) {
      dispatch(setYear(years[0]));
      dispatch(setComparingYear(years[0]))
    }
    const selectedCity = getSelectedCity(getState());
    if (selectedCity) {
      dispatch(setCity(selectedCity))
    }

    // We finally update the URL
    dispatch(updateUrl());
  };
}


/**
 * Set the current area
 * @param {{ label: string, value: string }} area Code of the area
 */
export const setArea = (area, resetDefault = false) => (dispatch, getState) => {
  const products = getValidProducts(getState());
  const productsFiltered = products.filter(p => p.domain.code === area.value && p.id === 'd5409e1d-1317-4519-b312-9f4c2ca48118');
  const product = productsFiltered[0];
  if (product) {
    dispatch(setProductType(product));
  }

};


/**
 * Set the current pollutant
 * @param {{ label: string, value: string }} area Code of the area
 */
export const setPollutant = (pollutant, defaultSector, resetDefault = false) => (dispatch, getState) => {
  dispatch({ type: SET_POLLUTANT, payload: pollutant });
  const sectors = getSectors(getState());
  const selectedSector = getSelectedSector(getState());
  // We give:
  // - either a default value for the sector
  // - either we need to reset the sector because the option is not available anymore
  if (!selectedSector || resetDefault) {
    const targetDefaultSector = sectors.find(s => s.value === 'Total');

    const sector = targetDefaultSector || sectors[0];
    dispatch(setSector(sector));
  }

  const selectedCity = getSelectedCity(getState());
  if (selectedCity) {
    dispatch(setCity(selectedCity))
  }

  // We finally update the URL
  dispatch(updateUrl());
};

/**
 * Set the current sector
 * @param {{ label: string, value: string }} sector Sector
 */
export const setSector = sector => (dispatch, getState) => {
  if (!sector) return null;
  dispatch({ type: SET_SECTOR, payload: sector.value });
  const selectedCity = getSelectedCity(getState());
  if (selectedCity) {
    dispatch(setCity(selectedCity))
  }
  dispatch(updateUrl());
};

/**
 * Set the current city
 * @param {{ label: string, value: string }} city City
 */
export const setCity = (city) => (dispatch, getState) => {
  if (!city) return null;
  dispatch({ type: SET_CITY, payload: city });
  dispatch(setTimeSeries(city));
  dispatch(updateUrl());
};


export const setTimeSeries = (city) => (dispatch, getState) => {

  const productId = getSelectedProductType(getState()).value;
  const lat = city.location.lat;
  const long = city.location.long;
  const pollutant = getSelectedPollutant(getState()).value;
  const sector = getSelectedSector(getState()).value;

  if (city) {
    fetch(
      `${process.env.REACT_APP_API_URL}/product/${productId}/timeSeries/${long}/${lat}.json?variable=${pollutant}_${sector}`, { credentials: 'include' }
    )
      .then(res => {
        if (res.ok) return res.json();
        throw new Error(res.statusText);
      })
      .then(timeset => dispatch({ type: SET_TIMESET, payload: timeset }))

    dispatch(updateUrl());
  }
};

/**
 * Set the current language
 * @param {{ label: string, value: string }} language Language
 */
export const setLanguage = language => (dispatch) => {
  dispatch({ type: SET_LANGUAGE, payload: language.value });

  dispatch(updateUrl());
};

/**
 * Set the current year
 * @param {{ label: string, value: number }} year Year
 */
export const setYear = year => (dispatch, getState) => {
  if (year) {
    dispatch({ type: SET_YEAR, payload: year.value });

    const months = getMonths(getState());
    const selectedMonth = getSelectedMonth(getState());

    // We give:
    // - either a default value for the month
    // - either we need to reset the month because the option is not available anymore
    if (!selectedMonth) {
      dispatch(setMonth(months[0]));
    }

    // We finally update the URL
    dispatch(updateUrl());
  }

};

/**
 * Set the current month
 * @param {{ label: string, value: number }} month Month
 */
export const setMonth = month => (dispatch, getState) => {
  const year = getSelectedYear(getState());
  const comparingYear = getSelectedComparingYear(getState());
  const comparingMonth = getSelectedComparingMonth(getState());
  const comparingMonths = getComparingMonths(getState());

  dispatch({ type: SET_MONTH, payload: month.value });

  if (comparingYear && comparingMonth) {
    const date = new Date(Date.UTC(year.value, month.value, 1));
    const comparingDate = new Date(
      Date.UTC(comparingYear.value, comparingMonth.value, 1),
    );

    // If the user chooses a date greater than the comparing one,
    // we update the month or reset it
    if (+comparingDate <= +date) {
      if (month.value < 11) {
        const newComparingMonth = comparingMonths.find(
          m => m.value === month.value + 1,
        );
        dispatch(setComparingMonth(newComparingMonth));
      } else {
        dispatch(setComparingMonth(null));
      }
    }
  }

  // We finally update the URL
  dispatch(updateUrl());
};

/**
 * Set the current comparing year
 * @param {{ label: string, value: number }} year Year
 */
export const setComparingYear = year => (dispatch, getState) => {
  dispatch({ type: SET_COMPARING_YEAR, payload: year && year.value });

  if (!year) {
    dispatch(setComparingMonth(null));
    return;
  }

  const months = getComparingMonths(getState());
  const selectedMonth = getSelectedComparingMonth(getState());

  // We give:
  // - either a default value for the month
  // - either we need to reset the month because the option is not available anymore
  if (!selectedMonth) {
    dispatch(setComparingMonth(months.length ? months[0] : null));
  }

  // We finally update the URL
  dispatch(updateUrl());
};

/**
 * Set the current comparing month
 * @param {{ label: string, value: number }} month Month
 */
export const setComparingMonth = month => (dispatch) => {
  dispatch({ type: SET_COMPARING_MONTH, payload: month && month.value });

  dispatch(updateUrl());
};

/**
 * Start the comparison by setting the comparing year
 */
export const startComparing = () => (dispatch, getState) => {
  const comparingYears = getComparingYears(getState());
  dispatch(setComparingYear(comparingYears[0]));

  // We finally update the URL
  dispatch(updateUrl());
};

/**
 * Stop the comparison by resetting the comparing year
 */
export const stopComparing = () => (dispatch) => {
  dispatch(setComparingYear(null));

  // We finally update the URL
  dispatch(updateUrl());
};

/**
 * Update the URL according to the state of the demo
 */
export const updateUrl = () => (dispatch, getState) => {
  const { emissionsDemo } = getState();
  const { state } = emissionsDemo;
  const {
    productId,
    pollutant,
    sector,
    year,
    month,
    comparingYear,
    comparingMonth,
    basemap,
    labels,
    language,
    viewport,
    city,
  } = state;

  const date = year && month !== null
    ? new Date(Date.UTC(year, month, 1))
    : null;

  const comparingDate = comparingYear && comparingMonth !== null
    ? new Date(Date.UTC(comparingYear, comparingMonth, 1))
    : null;

  const params = {
    id: productId,
    pollutant: JSON.stringify(pollutant),
    sector,
    date: date ? date.toISOString() : null,
    comparingDate: comparingDate ? comparingDate.toISOString() : null,
    basemap,
    labels,
    language,
    viewport: JSON.stringify(viewport),
    city
  };

  const search = Object
    .keys(params)
    .reduce(
      (res, key) => (params[key] ? `${res}${key}=${params[key]}&` : res),
      '?',
    )
    .slice(0, -1);

  dispatch(replace({ search }));
};

/**
 * Set the state attribute of the store
 * @param {{ [key: string]: string|number }} newState New state
 */
export const setState = newState => (dispatch) => {
  const acceptedKeys = Object.keys(defaultState.state);

  if (newState.pollutant) {
    newState.pollutant = JSON.parse(decodeURIComponent(newState.pollutant));
  }

  if (newState.viewport) {
    newState.viewport = JSON.parse(decodeURIComponent(newState.viewport));
  }

  dispatch({ type: SET_STATE, payload: pick(newState, acceptedKeys) });

  dispatch(updateUrl());
};

/**
 * Restore the state of the demo from the URL
 */
export const restoreState = () => (dispatch, getState) => {
  const { router } = getState();
  const { location } = router;
  const { search } = location;

  if (search.length < 2) return;

  // URL params deserialized
  const params = {};

  const keyValues = search.slice(1, search.length).split('&');
  for (let i = 0, j = keyValues.length; i < j; i++) {
    const keyValue = keyValues[i];
    const split = keyValue.split('=');

    // If we have a malformed key-value, we skip it
    if (split.length !== 2) continue;

    // eslint-disable-line no-continue
    const key = split[0];
    const value = split[1];

    if (key === 'id') {
      params.productId = value;
    } else if (key === 'date') {
      try {
        const date = new Date(value);
        params.year = date.getUTCFullYear();
        params.month = date.getUTCMonth();
      } catch (err) {
        console.error('Unable to restore the date', err);
      }
    } else if (key === 'comparingDate') {
      try {
        const date = new Date(value);
        params.comparingYear = date.getUTCFullYear();
        params.comparingMonth = date.getUTCMonth();
      } catch (err) {
        console.error('Unable to restore the comparing date', err);
      }
    } else {
      params[key] = value;
    }
  }

  // If there's not productId, we don't restore the state because later
  // we use this attribute to determine whether we're restoring or not
  // in order to perform a check on the values
  if (params.productId) {
    dispatch(setState(params));
  }
};

/**
 * Set the basemap
 * @param {string} basemap Basemap ID
 */
export const setBasemap = basemap => (dispatch) => {
  dispatch({ type: SET_BASEMAP, payload: basemap });

  dispatch(updateUrl());
};

/**
 * Set the labels
 * @param {string} labels labels ID
 */
export const setLabels = labels => (dispatch) => {
  dispatch({ type: SET_LABELS, payload: labels });

  dispatch(updateUrl());
};

export const setViewport = viewport => (dispatch) => {
  dispatch({ type: SET_VIEWPORT, payload: viewport });

  dispatch(updateUrl());
};



// WEBPACK FOOTER //
// ./src/components/emissions-demo/actions.js